import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, EmptyRow, Field, Loading, Modal, Page, Pager, Tabs } from "../components/ui";
import { label, PAGE_SIZE, qs, setQuery, when } from "../format";

const EMPTY = { title: "", slug: "", type: "page", body: "", status: "draft" };

function formFrom(row) {
  if (!row) return EMPTY;
  return {
    title: row.title || "",
    slug: row.slug || "",
    type: row.type || "page",
    body: row.body || "",
    status: row.status || "draft",
  };
}

function bodyLabel(type) {
  if (type === "faq") return "Answer";
  if (type === "announcement") return "Banner text";
  return "Page text";
}

export default function Content() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const type = params.get("type") || "";
  const status = params.get("status") || "";
  const list = useFetch("/content" + qs({ page, limit: PAGE_SIZE, type, status }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [editor, setEditor] = useState(null);
  const [notice, setNotice] = useState("");

  return (
    <Page
      title="Content"
      text="Published pages open on the website at /pages/slug. Published FAQs show on /faq. Published announcements are the home banners. Drafts stay in this panel."
      actions={<button type="button" className="btn" onClick={() => setEditor(EMPTY)}>New content</button>}
    >
      <Alerts error={list.error} notice={notice} />
      <Tabs
        value={type}
        onChange={(value) => setQuery(params, setParams, { type: value, page: "" })}
        options={[
          { value: "", label: "All types" },
          { value: "page", label: "Pages" },
          { value: "faq", label: "FAQ" },
          { value: "announcement", label: "Announcements" },
        ]}
      />
      <Tabs
        value={status}
        onChange={(value) => setQuery(params, setParams, { status: value, page: "" })}
        options={[
          { value: "", label: "Any status" },
          { value: "draft", label: "Draft" },
          { value: "published", label: "Published" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Title</th><th>Slug</th><th>Type</th><th>Status</th><th>Updated</th></tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => setEditor(row)}>
                  <td>{row.title}</td>
                  <td className="code">{row.slug}</td>
                  <td>{label(row.type)}</td>
                  <td><Badge value={row.status} /></td>
                  <td>{when(row.updatedAt)}</td>
                </tr>
              )) : <EmptyRow cols={5} text="No content" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
      {editor ? (
        <ContentForm
          key={editor.id || "new"}
          initial={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            setEditor(null);
            setNotice(message);
            list.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function ContentForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(formFrom(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setErrors({});
    const body = {
      title: form.title.trim(),
      type: form.type,
      body: form.body.trim(),
      status: form.status,
    };
    if (form.slug.trim()) body.slug = form.slug.trim();
    try {
      if (initial) await api("/content/" + initial.id, { method: "PATCH", body });
      else await api("/content", { method: "POST", body });
      onSaved(initial ? "Content saved" : "Content created");
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={initial ? "Edit content" : "New content"} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Alerts error={error} />
        <div className="stack">
          <Field label="Title" error={errors.title}>
            <input maxLength={120} value={form.title} onChange={(event) => set("title", event.target.value)} />
          </Field>
          <Field label="Slug" error={errors.slug} hint="Lowercase, for example about. Leave blank on create and it is built from the title.">
            <input value={form.slug} onChange={(event) => set("slug", event.target.value.toLowerCase())} />
          </Field>
          <Field label="Type" error={errors.type}>
            <select value={form.type} onChange={(event) => set("type", event.target.value)}>
              <option value="page">Page</option>
              <option value="faq">FAQ</option>
              <option value="announcement">Announcement</option>
            </select>
          </Field>
          <Field label={bodyLabel(form.type)} error={errors.body}>
            <textarea value={form.body} onChange={(event) => set("body", event.target.value)} />
          </Field>
          <Field label="Status" error={errors.status}>
            <select value={form.status} onChange={(event) => set("status", event.target.value)}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </Field>
        </div>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? "Saving..." : "Save content"}</button>
        </div>
      </form>
    </Modal>
  );
}
